import { theme } from '../../../theme';
import { CASE_STATUS_LABELS } from '../../assessment-history/types/assessmentHistory.types';
import type { StatusCount } from '../types/dashboard.types';

type StatusBreakdownProps = {
  statusCounts: StatusCount[];
  total: number;
};

const STATUS_COLORS: Record<string, string> = {
  new: 'dark:bg-blue-400 bg-blue-500',
  under_review: 'dark:bg-purple-400 bg-purple-500',
  referred_to_doctor: 'dark:bg-amber-400 bg-amber-500',
  awaiting_clearance: 'dark:bg-yellow-400 bg-yellow-500',
  cleared_to_play: 'dark:bg-emerald-400 bg-emerald-500',
  closed: 'dark:bg-gray-400 bg-gray-500',
};

const STATUS_TEXT_COLORS: Record<string, string> = {
  new: 'dark:text-blue-400 text-blue-600',
  under_review: 'dark:text-purple-400 text-purple-600',
  referred_to_doctor: 'dark:text-amber-400 text-amber-600',
  awaiting_clearance: 'dark:text-yellow-400 text-yellow-600',
  cleared_to_play: 'dark:text-emerald-400 text-emerald-600',
  closed: 'dark:text-gray-400 text-gray-500',
};

export const StatusBreakdown = ({ statusCounts, total }: StatusBreakdownProps) => {
  const activeStatuses = statusCounts.filter((s) => s.count > 0);

  return (
    <div className={theme.card.padded}>
      <h3 className={`text-sm font-semibold ${theme.text.heading} mb-4`}>Case Status Pipeline</h3>

      {/* Stacked bar */}
      {total > 0 && (
        <div className="flex h-3 rounded-full overflow-hidden mb-4 dark:bg-white/[0.06] bg-gray-100">
          {statusCounts.map(({ status, count }) => {
            if (count === 0) return null;
            const pct = (count / total) * 100;
            return (
              <div
                key={status}
                className={`${STATUS_COLORS[status]} transition-all duration-700 ease-out`}
                style={{ width: `${pct}%` }}
                title={`${CASE_STATUS_LABELS[status]}: ${count}`}
              />
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="grid grid-cols-2 gap-2">
        {statusCounts.map(({ status, count }) => (
          <div key={status} className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${STATUS_COLORS[status]}`} />
            <span className={`text-xs ${theme.text.muted} truncate`}>{CASE_STATUS_LABELS[status]}</span>
            <span className={`text-xs font-bold ml-auto ${count > 0 ? STATUS_TEXT_COLORS[status] : theme.text.veryMuted}`}>{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
